import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { enUS, svSE, type Localization } from "@mui/material/locale";
import { i18n } from "./instance";
import {
  LANGUAGE_STORAGE_KEY,
  SOURCE_LANGUAGE,
  type CmsLanguage,
} from "./languages";

const MUI_LOCALES: Record<CmsLanguage, Localization> = { en: enUS, sv: svSE };

interface LanguageCtx {
  language: CmsLanguage;
  /** The languages this build offers; one entry means no switcher. */
  languages: readonly CmsLanguage[];
  setLanguage: (language: CmsLanguage) => Promise<void>;
  muiLocale: Localization;
}

const Ctx = createContext<LanguageCtx>({
  language: SOURCE_LANGUAGE,
  languages: [SOURCE_LANGUAGE],
  setLanguage: async () => {},
  muiLocale: enUS,
});

export function useLanguage(): LanguageCtx {
  return useContext(Ctx);
}

function persist(language: CmsLanguage): void {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    // Private mode or blocked storage: the choice lasts until reload.
  }
}

/**
 * Owns the CMS language. Switching loads the new language's namespaces first
 * and then re-renders every `useTranslation()` consumer; nothing reloads.
 */
export function LanguageProvider({
  initial,
  languages,
  children,
}: {
  initial: CmsLanguage;
  languages: readonly CmsLanguage[];
  children: ReactNode;
}) {
  const [language, setState] = useState<CmsLanguage>(initial);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = useCallback(
    async (next: CmsLanguage) => {
      if (!languages.includes(next)) return;
      await i18n.changeLanguage(next);
      persist(next);
      setState(next);
    },
    [languages]
  );

  const value = useMemo<LanguageCtx>(
    () => ({
      language,
      languages,
      setLanguage,
      muiLocale: MUI_LOCALES[language],
    }),
    [language, languages, setLanguage]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
